import type { ItineraryReferenceMatch } from '@travel-planner/shared/schemas';
import type { ServerSearchHookResult } from '@/shared/ui';
import { trpc } from '@/shared/api/trpc';

// Itinerary picker search, shaped to the ServerSearchSelect `useSearch(query,
// open)` contract. Only fires when open AND the query is >= 2 chars (the
// server input requires min(2)). Reuses the itineraries.searchNext endpoint,
// which any itinerary-reading role (incl. consulting reporters) may call.
export const useItinerarySearch = (
  query: string,
  open: boolean
): ServerSearchHookResult<ItineraryReferenceMatch> => {
  const trimmed = query.trim();
  const result = trpc.itineraries.searchNext.useQuery(
    { reference: trimmed, limit: 10 },
    { enabled: open && trimmed.length >= 2 }
  );
  return { data: result.data, isLoading: result.isLoading };
};

// Pre-seeded picker: with no query, show the itineraries DETECTED on the
// current page (ids → refs, resolved server-side + deduped); once the reporter
// types >= 2 chars fall back to the normal reference search. Factory so the
// detected ids are closed over while still matching the ServerSearchSelect
// `useSearch(query, open)` contract.
export const makeSeededItinerarySearch = (detectedIds: readonly string[]) => {
  const ids = [...detectedIds];
  const useSeededItinerarySearch = (
    query: string,
    open: boolean
  ): ServerSearchHookResult<ItineraryReferenceMatch> => {
    const trimmed = query.trim();
    const searching = trimmed.length >= 2;
    const seeded = trpc.itineraries.referencesByIds.useQuery(
      { ids },
      { enabled: open && !searching && ids.length > 0 }
    );
    const searched = trpc.itineraries.searchNext.useQuery(
      { reference: trimmed, limit: 10 },
      { enabled: open && searching }
    );
    return searching
      ? { data: searched.data, isLoading: searched.isLoading }
      : { data: seeded.data, isLoading: seeded.isLoading };
  };
  return useSeededItinerarySearch;
};

// "PT65959 · 2600096" — the OptiGest number rides alongside the reference so a
// reporter who searched by OptiGest can see WHY a row matched, and can tell two
// otherwise identically-titled trips apart. Falls back to whichever identifier
// exists. Shared by the option list and the closed trigger so the label a
// reporter picked is the label they keep seeing.
export const itineraryOptionLabel = (
  match: ItineraryReferenceMatch
): string => {
  const ref = match.itineraryRef ?? match.title ?? match.id;
  const optigest = match.accountingReference?.trim();
  return optigest ? `${ref} · ${optigest}` : ref;
};

// Resolve one itinerary id → its label for the picker's closed-state trigger
// after a selection.
export const useItineraryLabel = (id: string | null): string | undefined => {
  const result = trpc.itineraries.referencesByIds.useQuery(
    { ids: id ? [id] : [] },
    { enabled: Boolean(id) }
  );
  const match = result.data?.[0];
  return match ? itineraryOptionLabel(match) : undefined;
};
